import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Form from "../../../components/Form";
import { SubmitHandler, FormHandles } from "@unform/core";
import Input, {
  MaterialInputEvent,
  MaterialTextFieldElement,
} from "../../../components/v2/Input";
import Button from "../../../components/Button";
import Layout from "../../../components/v2/Layout";
import ItemTipoReceita from "../../../components/v2/ItemTipoReceita";
import Format from "../../../helpers/Format";
import { isCpfOrCnpj } from "../../../helpers/Validates";
import {
  cpfOrCnpjMask,
  normalizeCpfOrCnpj,
  onlyAlphanumeric,
} from "../../../helpers/Masks";
import ConsultarService, { ResponseDebitos } from "../../../services/ConsultarService";
import { isServerError } from "../../../config/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import style from "./styles.module.scss";
import Show from "../../../components/Show";
import OverMaxDebts from "../../../components/OverMaxDebts";
import TextLink from "../../../components/TextLink";



interface FormData {
  documento: string;
  tipo: string;
  valor: string;
}

type TipoOpcao = "document" | "administrative" | "cda" | "judicial";

type TipoOpcional = Exclude<TipoOpcao, "document">;

const tiposOpcionais: TipoOpcional[] = ["administrative", "cda", "judicial"];
const keyI18n = "translations.consultDebts.view";

export default function ConsultarDebitosNew() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);
  const [data, setData] = useState<ResponseDebitos>();
  const [documentError, setDocumentError] = useState("");
  const [hasSearchError, setHasSearchError] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [totalInscricoes, setTotalInscricoes] = useState(0);
  const [totalSaldoDevedor, setTotalSaldoDevedor] = useState(0);
  const [documento, setDocumento] = useState("");
  const [show, setShow] = useState(false);
  const [tabname , setTabname] = useState("");
  const [value, setValue] = useState(0);
  const [totalTributesbalance, setTotalTributesBalance] = useState<Array<any>>([]);
  const [totalTributesbalanceRefis, setTotalTributesBalanceRefis] = useState<Array<any>>([]);
  const [overMaxDebts, setOverMaxDebts] = useState(false);
  const [overMaxDebtsData, setOverMaxDebtsData] = useState<any>();
  const [lastSearch, setLastSearch] = useState<FormData>({ documento: "", tipo: "document", valor: "" });
  const [lastAutomaticallyClearedField, setLastAutomaticallyClearedField] = useState<TipoOpcional | null>(null);

  const contributorName = data?.contributor_name_without_mask;

  const formRef = useRef<FormHandles>(null);
  const documentInputRef = useRef<MaterialTextFieldElement>(null);
  const administrativeInputRef = useRef<MaterialTextFieldElement>(null);
  const cdaInputRef = useRef<MaterialTextFieldElement>(null);
  const judicialInputRef = useRef<MaterialTextFieldElement>(null);

  const optionalInputRefs: Record<TipoOpcional, React.RefObject<MaterialTextFieldElement>> = {
    administrative: administrativeInputRef,
    cda: cdaInputRef,
    judicial: judicialInputRef,
  };

  const clearOptionalInputs = (activeType?: TipoOpcional, showFeedback = false) => {
    let clearedField: TipoOpcional | null = null;

    tiposOpcionais.forEach((type) => {
      const input = optionalInputRefs[type].current;

      if (type !== activeType && input?.value) {
        input.value = "";
        input.dispatchEvent(new Event("input", { bubbles: true, composed: true }));
        clearedField = type;
      }
    });

    if (showFeedback && clearedField) {
      setLastAutomaticallyClearedField(clearedField);
    } else if (!activeType) {
      setLastAutomaticallyClearedField(null);
    }
  };

  const handleOptionalInput = (activeType: TipoOpcional) => (
    event: MaterialInputEvent,
  ) => {
    if (event.currentTarget.value) {
      clearOptionalInputs(activeType, true);
    }
  };

  const getSearchData = (): FormData => {
    const optionalFilter = tiposOpcionais
      .map((type) => ({
        type,
        value: optionalInputRefs[type].current?.value.trim() || "",
      }))
      .find(({ value: optionalValue }) => Boolean(optionalValue));

    return {
      documento: normalizeCpfOrCnpj(documentInputRef.current?.value || ""),
      tipo: optionalFilter?.type || "document",
      valor: optionalFilter?.value || "",
    };
  };

  const handleSubmitSemFiltro = () => {
    if (formRef.current) {
      clearOptionalInputs();
      formRef.current.submitForm();
    }
  };

  const handleReloadSearch = () => {
    formRef.current?.submitForm();
  };

  const handleFormKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const isComposing = (event.nativeEvent as KeyboardEvent).isComposing;
    const target = event.target as HTMLElement;
    const isTextField = Boolean(
      target.closest("md-filled-text-field, md-outlined-text-field"),
    );

    if (
      event.key !== "Enter"
      || event.shiftKey
      || event.ctrlKey
      || event.altKey
      || event.metaKey
      || isComposing
      || loading
      || !isTextField
    ) {
      return;
    }

    event.preventDefault();
    formRef.current?.submitForm();
  };

  const submitAttendance = async () => {
    const contributorDocment = normalizeCpfOrCnpj(documentInputRef.current?.value || "");

    const contributor_document = contributorDocment;
    const contributor_attendance_histories = {'attendance_place':'Portal do Contribuinte - Consultar Dívidas',
                                              'attendant':'Assitente Virtual',
                                              'attendance_subject':'portal_of_taxpayer_consult_debt'};

    const attendanceData = {contributor_document, contributor_attendance_histories}

    await ConsultarService
      .attendanceRegistration(attendanceData)

  };

  const handleSubmit: SubmitHandler<FormData> = async () => {
    const formData = getSearchData();

    try {
      setDocumentError("");

      formRef.current?.setErrors({});

      const schema = Yup.object<FormData>().shape({
        documento: Yup.string()
          .test("cpfOrCnpj", t(`${keyI18n}.validation.invalidDocument`), (value) => {
            if (typeof value === "string") {
              return isCpfOrCnpj(value);
            }

            return false;
          })
          .required(t(`${keyI18n}.validation.requiredDocument`)),
        tipo: Yup.string().required(t(`${keyI18n}.validation.requiredType`)),
        valor: Yup.string().when("tipo", {
          is: (value) => value !== "document",
          then: Yup.string().required(t(`${keyI18n}.validation.requiredField`)),
          otherwise: Yup.string().notRequired(),
        }),
      });

      await schema.validate(formData, {
        abortEarly: false,
      });

      setData(undefined);
      setHasSearchError(false);
      setPaymentError("");
      setOverMaxDebts(false);
      setOverMaxDebtsData(undefined);
      setHasSearched(true);
      setIsFormCollapsed(true);
      setLoading(true);

      await ConsultarService
        .debitos(formData.documento, formData.tipo, formData.valor)
        .then((response) => {
          if (response.over_max_debts) {
            setOverMaxDebts(true);
            setOverMaxDebtsData(response);
          } else {
            setOverMaxDebts(false);
            setOverMaxDebtsData('');
            setData(response);
            if (response.is_update) {
              submitAttendance();
            }

            if(response){
              const currentTab = Object.keys(response.data)[0]
              document.getElementById(currentTab)?.classList.add(style.tabTipoReceitaSelectedConsultDebts)
              document.getElementById(currentTab+"Mobile")?.classList.add(style.tabTipoReceitaSelectedConsultDebts)
              setTabname(currentTab)
            }

            let totalIns = 0;
            let totalSaldo = 0;
            let totalPertribute: {type: string, balance: number};
            let totalPertributeRefis: {type: string, balance: number};
            let groupTributes: Array<any> = [];
            let groupTributesRefis: Array<any> = [];
            let tributeBalance;
            let tributeBalanceRefis;

            Object.keys(response.data).forEach((tipo) => {
              const situacoes = Object.keys(response.data[tipo]);
              totalIns += response.data[tipo][situacoes[0]].total_inscriptions;
              totalSaldo += response.data[tipo][situacoes[0]].total_balance;
              tributeBalance = response.data[tipo][situacoes[0]].total_balance;
              tributeBalanceRefis = response.data[tipo][situacoes[0]].balance_to_legal_action;
              totalPertribute = {type: tipo, balance: tributeBalance};
              totalPertributeRefis = {type: tipo, balance: tributeBalanceRefis};
              groupTributes.push(totalPertribute)
              groupTributesRefis.push(totalPertributeRefis)
              tributeBalance = 0;
              tributeBalanceRefis= 0;
            });
            setTotalTributesBalance(groupTributes);
            setTotalTributesBalanceRefis(groupTributesRefis);
            setTotalInscricoes(totalIns);
            setTotalSaldoDevedor(totalSaldo);
            setDocumento(formData.documento);
            setLastSearch(formData);
          }
        }).catch((err) => {
          setHasSearchError(true);
          setIsFormCollapsed(false);

          if (isServerError(err)) {
            if (err.error == "Débitos não encontrados para os dados informados."){
              submitAttendance();
            }
          } else {
            console.error(err);
          }
        }).finally(() => {
          setLoading(false);
        });
    } catch (err) {
      setLoading(false);
      if (err instanceof Yup.ValidationError) {
        const errors = err.inner.reduce<{}>((o, c) => ({ ...o, [c.path]: c.message }), {});
        formRef.current?.setErrors(errors);
        const validationError = err.inner.find(({ path }) => path === "documento");
        setDocumentError(validationError?.message || "");
      }
    }
  };

  const ActiveTab = (e: { currentTarget: { id: any; }; }) => {
    var tabName = e.currentTarget.id
    setTabname(tabName);
    setShow(!show)
    const selectedtab = document.querySelectorAll(`.${style.tabTipoReceitaConsultDebts}`)
    Array.from(selectedtab).map((item) => {
      item.classList.remove(style.tabTipoReceitaSelectedConsultDebts)
    })
    const elementId = document.getElementById(tabName);
    elementId?.classList.add(style.tabTipoReceitaSelectedConsultDebts);
  }

  const ActiveTabMobile = (e: { currentTarget: { id: any; }; }) => {
    var tabName = e.currentTarget.id
    setTabname(tabName);
    setShow(!show)
    const selectedtab = document.querySelectorAll(`.${style.tabTipoReceitaMobileConsultDebts}`)
    Array.from(selectedtab).map((item) => {
      item.classList.remove(style.tabTipoReceitaSelectedConsultDebts)
    })
    const elementId = document.getElementById(tabName);
    elementId?.classList.add(style.tabTipoReceitaSelectedConsultDebts);
  }

  const MoveCarousel = (direction: "left" | "right") => {
    const lenghtofElement = document.querySelectorAll("[data-carousel-item]").length
    var actualValue = value
    const maxValue = 201 * (lenghtofElement - 1)
    if (direction === "left" && value != 0) {
      setValue(actualValue + 201)
    }else if(direction === "right" && value != (maxValue * -1)) {
      setValue(actualValue - 201)
    }
  }

  return (
    <Layout>
      <section
        className={[
          style.search,
          isFormCollapsed ? style.searchCollapsed : "",
        ].filter(Boolean).join(" ")}
      >
        <div
          className={[
            style.headingContainer,
            isFormCollapsed ? style.headingContainerCollapsed : "",
          ].filter(Boolean).join(" ")}
        >
          <div className={style.headingText}>
            <h3 className={`pgm-title-large ${style.headingTitle}`}>
              {t(`${keyI18n}.title`)}
            </h3>
            {!isFormCollapsed && (
              <p className={`pgm-body-large ${style.headingDescription}`}>
                {t(`${keyI18n}.description`)}
              </p>
            )}
          </div>
          <Button
            variant="text"
            type="button"
            iconButton
            className={style.collapseButton}
            aria-label={t(`${keyI18n}.aria.${isFormCollapsed ? "expandForm" : "collapseForm"}`)}
            aria-controls="consult-debts-form"
            aria-expanded={!isFormCollapsed}
            onClick={() => setIsFormCollapsed((collapsed) => !collapsed)}
            tooltip={t(`${keyI18n}.tooltips.${isFormCollapsed ? "maximizeForm" : "minimizeForm"}`)}
            tooltipPlacement="bottom"
            icon={(
              <span className={`material-symbols-sharp ${style.collapseIcon}`}>
                {isFormCollapsed ? "arrow_drop_up" : "arrow_drop_down"}
              </span>
            )}
          />
        </div>
        <Form
          ref={formRef}
          id="consult-debts-form"
          onSubmit={handleSubmit}
          onKeyDown={handleFormKeyDown}
          className={style.form}
          hidden={isFormCollapsed}
          noValidate
        >
          <div className={style.inputContainer}>
            <div className={[
              style.inputField,
              documentError ? style.inputFieldWithMessage : "",
            ].filter(Boolean).join(" ")}>
              <Input
                ref={documentInputRef}
                className={style.formInput}
                fullWidth
                id="contributorDocment"
                name="documento"
                label={t(`${keyI18n}.fields.document`)}
                mask={cpfOrCnpjMask}
                required
                error={Boolean(documentError)}
                errorText={documentError}
                clearButtonAriaLabel={t(`${keyI18n}.aria.clearField`, {
                  field: t(`${keyI18n}.fields.document`),
                })}
                onInput={() => setDocumentError("")}
              />
            </div>
            <div className={[
              style.inputField,
              lastAutomaticallyClearedField === "administrative"
                ? style.inputFieldWithMessage
                : "",
            ].filter(Boolean).join(" ")}>
              <Input
                ref={administrativeInputRef}
                className={style.formInput}
                fullWidth
                id="administrativeInscription"
                name="administrative"
                label={t(`${keyI18n}.fields.administrativeInscription`)}
                mask={onlyAlphanumeric}
                supportingText={lastAutomaticallyClearedField === "administrative"
                  ? t(`${keyI18n}.fields.onlyOneOptional`)
                  : undefined}
                clearButtonAriaLabel={t(`${keyI18n}.aria.clearField`, {
                  field: t(`${keyI18n}.fields.administrativeInscription`),
                })}
                onInput={handleOptionalInput("administrative")}
              />
            </div>
          </div>
          <div className={style.inputContainer}>
            <Input
              ref={cdaInputRef}
              className={style.formInput}
              fullWidth
              id="cda"
              name="cda"
              label={t(`${keyI18n}.fields.cda`)}
              mask={onlyAlphanumeric}
              supportingText={lastAutomaticallyClearedField === "cda"
                ? t(`${keyI18n}.fields.onlyOneOptional`)
                : undefined}
              clearButtonAriaLabel={t(`${keyI18n}.aria.clearField`, {
                field: t(`${keyI18n}.fields.cda`),
              })}
              onInput={handleOptionalInput("cda")}
            />
            <Input
              ref={judicialInputRef}
              className={style.formInput}
              fullWidth
              id="judicialProcess"
              name="judicial"
              label={t(`${keyI18n}.fields.judicialProcess`)}
              mask={onlyAlphanumeric}
              supportingText={lastAutomaticallyClearedField === "judicial"
                ? t(`${keyI18n}.fields.onlyOneOptional`)
                : undefined}
              clearButtonAriaLabel={t(`${keyI18n}.aria.clearField`, {
                field: t(`${keyI18n}.fields.judicialProcess`),
              })}
              onInput={handleOptionalInput("judicial")}
            />
          </div>
          <div className={style.submitContainer}>
            <p className={`pgm-body-large ${style.infoRefis}`}>
              {t(`${keyI18n}.sefin.description`)}{" "}
              <TextLink
                link="https://www.sefin.fortaleza.ce.gov.br/"
                target="_blank"
                text={t(`${keyI18n}.sefin.link`)}
              />
            </p>
            <Button
              variant="tonal"
              type="submit"
              disabled={loading}
              icon={(
                <span className={`material-symbols-sharp ${style.searchIcon}`}>
                  search
                </span>
              )}
            >
              {t(`${keyI18n}.actions.search`)}
            </Button>
          </div>
        </Form>
      </section>
      {!hasSearched && !loading && (
        <div className={style.initialMessage}>
          <span
            className={`material-symbols-sharp ${style.initialMessageIcon}`}
            aria-hidden="true"
          >
            mintmark
          </span>
          <div className={style.initialMessageText}>
            <p className="pgm-headline-small">
              {t(`${keyI18n}.initialMessage.document`)}
            </p>
            <p className="pgm-headline-small">
              {t(`${keyI18n}.initialMessage.optionalField`)}
            </p>
          </div>
        </div>
      )}
      {hasSearchError && !loading && (
        <div className={style.initialMessage} role="alert" aria-live="assertive">
          <span
            className={`material-symbols-sharp ${style.initialMessageIcon}`}
            aria-hidden="true"
          >
            search_off
          </span>
          <div className={style.initialMessageText}>
            <p className="pgm-headline-small">
              {t(`${keyI18n}.errors.title`)}
            </p>
            <p className="pgm-headline-small">
              {t(`${keyI18n}.errors.description`)}
            </p>
          </div>
        </div>
      )}
      <div className={style.resultsContainer} aria-busy={loading}>
      {loading && (
        <div className={style.loadingState} role="status" aria-live="polite">
          <span className={style.loadingIndicator} aria-hidden="true" />
          <p className={`pgm-title-medium ${style.loadingText}`}>
            {t(`${keyI18n}.loading`)}
          </p>
        </div>
      )}
      {overMaxDebts && overMaxDebtsData &&
        <OverMaxDebts
          contributorName={overMaxDebtsData.contributor_name}
          contributorDocument={overMaxDebtsData.contributor_document}
          totalBalance={overMaxDebtsData.balance}
          receiptTypes={overMaxDebtsData.receipt_types_in_debts}
        />
      }
      {data && (
        <div className={style.resultsHeader}>
          <div className={style.resultSummary}>
            <strong className={`pgm-title-medium ${style.contributorName}`}>
              {contributorName} - {cpfOrCnpjMask(documento).mask()}
            </strong>
            <div className={style.contributorTotals}>
              <span>
                <strong className={`pgm-title-medium ${style.contributorLabel}`}>
                  {t(`${keyI18n}.summary.openInscriptions`)}
                </strong>{" "}
                <span className={`pgm-body-large ${style.contributorValue}`}>{totalInscricoes}</span>
              </span>
              <span>
                <strong className={`pgm-title-medium ${style.contributorLabel}`}>
                  {t(`${keyI18n}.summary.balance`)}
                </strong>{" "}
                <span className={`pgm-body-large ${style.contributorValue}`}>
                  {Format.currency(totalSaldoDevedor)}
                </span>
              </span>
            </div>
          </div>
          <div className={style.resultsMessages}>
            {paymentError && (
              <div className={`pgm-body-medium ${style.paymentError}`} role="alert">
                <span
                  className={`material-symbols-sharp ${style.paymentErrorIcon}`}
                  aria-hidden="true"
                >
                  warning
                </span>
                <span>{paymentError}</span>
              </div>
            )}
            {data.more_debts && (
              <div className={style.otherDebts}>
                <div className={`pgm-body-medium ${style.otherDebtsMessage}`}>
                  <span
                    className={`material-symbols-sharp ${style.otherDebtsIcon}`}
                    aria-hidden="true"
                  >
                    info
                  </span>
                  <span>{t(`${keyI18n}.otherDebts.message`)}</span>
                </div>
                <Button
                  variant="filled"
                  size="small"
                  shape="square"
                  type="button"
                  onClick={handleSubmitSemFiltro}
                >
                  {t(`${keyI18n}.otherDebts.action`)}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      {!loading && data && (
        <div className={style.tabsContentItemsConsultDebts}>
          <span className={style.arrowLeftConsultDebts} onClick={() => {MoveCarousel("left")}}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </span>
          <div className={style.carouselVisibilityConsultDebts}>
            <div className={style.containerTabsConsultDebts} style={{transform: `translate(${(value)}px)`, transition: "transform 1s"}}>
              {data && Object.keys(data.data).map((tipoReceita) => (
                <div key={tipoReceita} data-carousel-item>
                  <div className={style.tipoReceita}>
                    <div className={style.tabTipoReceitaConsultDebts} id={tipoReceita} onClick={ActiveTab}>
                      <div className={style.recipTitle}>{tipoReceita}</div>
                      <Show if={tabname === tipoReceita}>
                        <div className={style.selectedTabConsultDebts}></div>
                      </Show>
                    </div>
                  </div>
                  <div>
                    <div className={style.tipoReceitaMobile}>
                      <div className={style.tabTipoReceitaMobileConsultDebts} id={tipoReceita+"Mobile"} onClick={ActiveTabMobile}>
                        <div className={style.recipTitle}>{tipoReceita}</div>
                        <Show if={tabname+"Mobile" === tipoReceita+"Mobile" || tabname === tipoReceita+"Mobile"}>
                          <div className={style.selectedTabConsultDebts}></div>
                        </Show>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <span className={style.arrowRightConsultDebts} onClick={() => {MoveCarousel("right")}}>
            <FontAwesomeIcon icon={faChevronRight} />
          </span>
        </div>
      )}
      {data && Object.keys(data.data).map((tipoReceita) => (
        <div key={tipoReceita} className={style.contentTypeConsultDebts}>
          <Show if={tabname === tipoReceita || tabname === tipoReceita+"Mobile"}>
            <ItemTipoReceita
              key={tipoReceita}
              documento={documento}
              title={tipoReceita}
              status={data.data[tipoReceita]}
              reloadSearch={handleReloadSearch}
              totalBalances={totalTributesbalance}
              totalBalancesRefis={totalTributesbalanceRefis}
              showAddress={false}
              consultType={lastSearch.tipo}
              consultValue={lastSearch.valor}
              onPaymentError={setPaymentError}
            />
          </Show>
        </div>
      ))}
      </div>
    </Layout>
  );
}
